/* eslint-disable max-lines */
import { isMobileApp } from "common/utils/browser";
import { ArrowLeft, Loader2, Shirt, Sparkles, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage } from "./ErrorMessage";
import { GradientButton } from "./GradientButton";
import type { GeneratedImage } from "./types";

type RefinementViewProps = {
  image: GeneratedImage;
  selectedOrientation: string;
  onRefineImage: (prompt: string) => Promise<void>;
  onSelectImage: (imageUrl: string, prompt: string) => Promise<void>;
  onExitEditing: () => void;
  isRefining: boolean;
  imageTransitioning: boolean;
  selectingImageUrl: string | null;
  error: string | null;
  onErrorDismiss: () => void;
  isMobile?: boolean;
};

export const RefinementView = ({
  image,
  selectedOrientation,
  onRefineImage,
  onSelectImage,
  onExitEditing,
  isRefining,
  imageTransitioning,
  selectingImageUrl,
  error,
  onErrorDismiss,
  isMobile = false
}: RefinementViewProps) => {
  const [editingPrompt, setEditingPrompt] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to component top when RefinementView opens (only on mobile and only if header not visible)
  useEffect(() => {
    if (isMobile) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (containerRef.current) {
          // Check if the component header is visible in viewport
          const rect = containerRef.current.getBoundingClientRect();
          // Header is visible if it's within the top 100px of the viewport
          const isHeaderVisible = rect.top >= 0 && rect.top <= 100;

          if (!isHeaderVisible) {
            // Scroll to the component top
            const scrollTop = window.pageYOffset + rect.top;
            window.scrollTo({
              top: Math.max(0, scrollTop - 20), // Small offset from top
              behavior: "smooth"
            });
          }
        }
      }, 50);
    }
  }, [isMobile]);

  const getAspectRatio = () => {
    if (selectedOrientation === "landscape") return "16/9";
    if (selectedOrientation === "portrait") return "9/16";
    return "1/1";
  };

  const handleRefine = async () => {
    if (!editingPrompt.trim()) return;
    await onRefineImage(editingPrompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isRefining && editingPrompt.trim()) {
      handleRefine();
    }
  };

  const clearPrompt = () => {
    setEditingPrompt("");
  };

  return (
    <div
      ref={containerRef}
      className="flex h-[calc(100vh-64px)] flex-col overflow-hidden bg-gray-50 text-gray-900" // 64px is height of masthead
    >
      <div className={`${isMobile ? "px-4 py-2" : "px-6 py-3"} border-b border-gray-200 bg-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="exit-editing-button"
              onClick={onExitEditing}
              className="rounded-lg p-2 transition-colors hover:bg-gray-100"
              aria-label="Go back to image selection"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1
              className={`${isMobile ? "text-xl" : "text-2xl"} flex items-center gap-2 font-bold`}
              style={isMobile ? { margin: 0, fontSize: "18px" } : {}}
            >
              <Sparkles className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} text-indigo-600`} />
              Refine Your Design
            </h1>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && <ErrorMessage error={error} onDismiss={onErrorDismiss} className="mx-4 mt-4" />}

      <div
        className={`flex items-center justify-center bg-[#F9FAFC] p-2 ${isMobileApp() ? "h-full" : "flex-1"}`}
      >
        <div className="flex w-full max-w-xl items-center justify-center">
          <div className="relative">
            <div
              className="overflow-hidden rounded-lg bg-white shadow-lg transition-all duration-300 ease-in-out"
              style={{
                aspectRatio: getAspectRatio(),
                maxHeight: "45vh",
                maxWidth: "100%"
              }}
            >
              <img
                key={image.url} // Force re-render on image change for animation
                src={image.url}
                alt="Design being edited"
                className="h-full w-full object-contain transition-all duration-300 ease-in-out"
              />
            </div>

            {(isRefining || imageTransitioning) && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center rounded-lg transition-opacity duration-200"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
              >
                <div className="mb-4 animate-pulse rounded-full bg-white p-3 shadow-lg">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                </div>
                <div className="rounded-lg bg-white bg-opacity-95 px-6 py-3 text-center text-sm font-medium text-gray-700 shadow-sm">
                  Refining your design
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 bg-white p-3">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-1 text-sm text-gray-600">
            Describe what you&apos;d like to change or add to this design:
          </p>
          <div className="flex flex-col gap-2 md:flex-row">
            <div className="relative flex-1">
              <input
                type="text"
                data-testid="editing-prompt-input"
                value={editingPrompt}
                onChange={e => setEditingPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., Make the colors more vibrant, add a vintage texture..."
                disabled={isRefining}
                className={`w-full rounded-sm border border-gray-300 bg-[#F9FAFC] px-4 py-4 text-base transition-all focus:outline-none`}
              />
              {editingPrompt && !isRefining && (
                <button
                  data-testid="clear-prompt-button"
                  onClick={clearPrompt}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <div className="flex justify-center gap-3">
              <GradientButton
                onClick={handleRefine}
                disabled={!editingPrompt.trim() || isRefining || selectingImageUrl !== null}
                style={
                  !editingPrompt.trim() || isRefining || selectingImageUrl !== null
                    ? {}
                    : { color: "white" }
                }
                data-testid="refine-design-button"
              >
                {isRefining ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Refining...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    Refine Design
                  </>
                )}
              </GradientButton>
              <button
                data-testid="select-refined-image-button"
                onClick={() => onSelectImage(image.url, editingPrompt)}
                disabled={isRefining || selectingImageUrl !== null}
                className="relative flex items-center justify-center gap-2 rounded-sm bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700 disabled:bg-gray-300 disabled:text-gray-400 md:flex-initial"
                style={isRefining || selectingImageUrl !== null ? {} : { color: "white" }}
              >
                {selectingImageUrl === image.url && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-indigo-600">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  </div>
                )}
                <Shirt className="h-4 w-4" />
                Choose This
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
